import { z } from 'zod'
import { ITEM_IDS } from './items.schema'
import type { ItemId } from './items.schema'

/** Per-race counters a daily task can track (D-038). */
export const BASE_METRICS = [
  'races',
  'finishes',
  'top2',
  'wins',
  'coins',
  'itemsUsed',
  'crates',
  'cleanFinishes',
  'hits',
] as const

export type BaseMetric = (typeof BASE_METRICS)[number]
/** A base counter, or uses of one item kind (`item:spike_ball`). */
export type Metric = BaseMetric | `item:${ItemId}`

/** Every metric, base counters first, then one per item id in `ITEM_IDS` order. */
export const METRICS: readonly Metric[] = [
  ...BASE_METRICS,
  ...ITEM_IDS.map((id): Metric => `item:${id}`),
]

export function isMetric(value: unknown): value is Metric {
  return typeof value === 'string' && (METRICS as readonly string[]).includes(value)
}

const count = z.number().int().min(0)
const positive = z.number().int().min(1)
/** Rewards by placement 1..4. */
const placement = z.array(count).length(4)

const metric = z.custom<Metric>(isMetric, {
  message: `metric must be one of ${BASE_METRICS.join(', ')} or item:<${ITEM_IDS.join('|')}>`,
})

const task = z.strictObject({
  id: z.string().regex(/^[a-z0-9_]{1,32}$/),
  text: z.string().min(1).max(48),
  metric,
  target: positive,
  coins: count,
  xp: count,
})

/** Raw `economy.json` (D-038). Economy numbers are not gameplay numbers: Phase 2 owns them. */
export const economyFileSchema = z.strictObject({
  xp: z.strictObject({
    placement,
    finish: count,
    perCoin: count,
    coinCap: count,
    perItemUsed: count,
    itemCap: count,
    /** XP per hit landed on another runner (D-053), capped at `hitCap` hits. */
    perHitLanded: count,
    hitCap: count,
  }),
  coins: z.strictObject({ placement, perCoin: count }),
  levels: z.strictObject({ baseXp: positive, stepXp: count, maxLevel: positive }),
  daily: z
    .strictObject({
      count: positive,
      tasks: z.array(task).superRefine((tasks, ctx) => {
        const seen = new Set<string>()
        tasks.forEach((t, i) => {
          if (seen.has(t.id)) {
            ctx.addIssue({ code: 'custom', path: [i, 'id'], message: `duplicate task id ${t.id}` })
          }
          seen.add(t.id)
        })
      }),
    })
    .refine((d) => d.count <= d.tasks.length, {
      path: ['count'],
      message: 'count must not exceed the number of tasks',
    }),
})

export type EconomyFile = z.infer<typeof economyFileSchema>
